import { useEffect, useRef, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Pencil, Plus, Trash2, X } from 'lucide-react';
import {
  ClientsResponse,
  SessionResponse,
  type Client,
  type Session,
  type UpdateGiftContactsRequest,
} from '@diana/contracts';
import { useAdminApi } from '../../app/AdminApiContext.js';
import { AdminApiError } from '../../lib/api-client.js';
import { Button, IconButton, useDianaUIPortal, useEditPanelViewport } from '../../ui/index.js';
import { ClientQuickSearch } from './ClientQuickSearch.js';
import { giftWorkingRole } from './crm-format.js';
import './GiftContactsPanel.css';

export function GiftContactsPanel({
  shoot,
  disabled,
  onSaved,
  onDirtyChange,
  onBusyChange,
}: {
  shoot: Session;
  disabled?: boolean;
  onSaved(shoot: Session): void;
  onDirtyChange?(value: boolean): void;
  onBusyChange?(value: boolean): void;
}) {
  const api = useAdminApi();
  const portal = useDianaUIPortal();
  const originalRecipient = shoot.giftContacts?.recipientId ?? null;
  const [open, setOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(originalRecipient);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const closeButton = useRef<HTMLButtonElement>(null);
  const newId = useRef('');
  const gate = useRef(false);
  const { panelRef, bodyRef } = useEditPanelViewport(open);
  const dirty = selectedId !== originalRecipient || Boolean(name);
  const selected = clients.find((client) => client.id === selectedId) ?? null;
  const search = name.normalize('NFKC').trim().toLocaleLowerCase();
  const exactMatch = clients.some(
    (client) =>
      !client.archivedAt &&
      [client.name, client.email, client.phone, client.instagram].some(
        (value) => value?.normalize('NFKC').trim().toLocaleLowerCase() === search,
      ),
  );
  const canCreate = Boolean(search) && !exactMatch && !loading && !loadError;
  const start = () => {
    setSelectedId(originalRecipient);
    setName('');
    setError('');
    newId.current = crypto.randomUUID();
    setOpen(true);
  };
  const close = () => {
    if (gate.current) return;
    if (dirty && !window.confirm('Discard unsaved recipient changes?')) return;
    setOpen(false);
  };
  useEffect(() => {
    onDirtyChange?.(open && dirty);
    return () => onDirtyChange?.(false);
  }, [open, dirty, onDirtyChange]);
  useEffect(() => {
    onBusyChange?.(busy);
    return () => onBusyChange?.(false);
  }, [busy, onBusyChange]);
  useEffect(() => {
    if (!open || (!dirty && !busy)) return;
    const before = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', before);
    return () => window.removeEventListener('beforeunload', before);
  }, [open, dirty, busy]);
  const load = async () => {
    setLoading(true);
    setLoadError(false);
    try {
      setClients((await api.get('/api/v1/admin/clients?archived=all', ClientsResponse)).clients);
    } catch {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    if (open) void load();
  }, [open]);
  const save = async (remove = false) => {
    if (gate.current) return;
    let recipient: UpdateGiftContactsRequest['recipient'] = null;
    if (!remove) {
      if (selectedId) recipient = { kind: 'existing', clientId: selectedId };
      else if (canCreate && name.trim().length <= 200)
        recipient = {
          kind: 'new',
          client: {
            id: newId.current,
            name: name.trim(),
            email: null,
            phone: null,
            instagram: null,
            notes: null,
          },
        };
      else {
        setError('Choose a suggested client or enter a new name.');
        return;
      }
    }
    gate.current = true;
    setBusy(true);
    setError('');
    try {
      const result = await api.request(`/api/v1/admin/sessions/${shoot.id}/gift-contacts`, {
        method: 'PUT',
        body: {
          expectedRevision: shoot.revision,
          recipient,
          mainContact: recipient ? giftWorkingRole(shoot.status) : 'buyer',
        },
        schema: SessionResponse,
      });
      setOpen(false);
      onSaved(result.session);
    } catch (problem) {
      setError(
        problem instanceof AdminApiError
          ? problem.status === 409
            ? 'Shoot changed. Reload the latest version before saving.'
            : problem.fields?.map((field) => field.message).join(' ') || problem.message
          : 'Could not save the recipient. Your changes are kept. Try again.',
      );
    } finally {
      gate.current = false;
      setBusy(false);
    }
  };
  return (
    <Dialog.Root open={open} onOpenChange={(value) => (value ? start() : close())}>
      <Dialog.Trigger asChild>
        {originalRecipient ? (
          <IconButton variant="quiet" label="Change recipient" icon={Pencil} disabled={disabled} />
        ) : (
          <Button variant="quiet" leadingIcon={Plus} disabled={disabled}>
            Add recipient
          </Button>
        )}
      </Dialog.Trigger>
      <Dialog.Portal container={portal}>
        <Dialog.Overlay className="shoot-panel__overlay" />
        <Dialog.Content
          ref={panelRef}
          className="shoot-panel gift-contacts-panel dui-compact-panel"
          data-compact-panel
          data-mode="gift-recipient"
          onEscapeKeyDown={(event) => {
            event.preventDefault();
            close();
          }}
          onPointerDownOutside={(event) => {
            event.preventDefault();
            close();
          }}
          onOpenAutoFocus={(event) => {
            if (window.matchMedia('(max-width:1023px)').matches) {
              event.preventDefault();
              closeButton.current?.focus({ preventScroll: true });
            }
          }}
        >
          <header className="shoot-panel__header" data-panel-header>
            <div>
              <Dialog.Title>Recipient</Dialog.Title>
              <Dialog.Description className="sr-only">
                Find a client or enter a new recipient name. Save explicitly to link or create the
                client.
              </Dialog.Description>
            </div>
            <IconButton
              ref={closeButton}
              variant="quiet"
              label="Close recipient"
              icon={X}
              onClick={close}
              disabled={busy}
            />
          </header>
          <div
            ref={bodyRef}
            className="shoot-panel__body gift-contacts-panel__body"
            data-panel-body
          >
            <ClientQuickSearch
              clients={clients}
              selected={selected}
              onSelect={(client) => {
                setSelectedId(client?.id ?? null);
                setName('');
              }}
              onQueryChange={setName}
              placeholder="Search or enter a name"
              emptyMessage="No client found. Add this name as a new recipient."
              disabled={busy}
              loading={loading}
              error={loadError}
              retry={() => void load()}
            />
            {canCreate ? (
              <p className="gift-contacts-panel__hint">
                A new client will be added as <strong>{name.trim()}</strong>.
              </p>
            ) : null}
          </div>
          <footer className="shoot-panel__actions gift-contacts-panel__footer" data-panel-footer>
            {error ? (
              <div role="alert">
                <p>{error}</p>
                {error.includes('Reload') ? (
                  <Button
                    variant="outline"
                    onClick={async () => {
                      if (!window.confirm('Discard your recipient changes and reload this shoot?'))
                        return;
                      gate.current = true;
                      setBusy(true);
                      try {
                        onSaved(
                          (await api.get(`/api/v1/admin/sessions/${shoot.id}`, SessionResponse))
                            .session,
                        );
                      } catch {
                        setError('Could not reload. Your changes are kept. Try again.');
                      } finally {
                        gate.current = false;
                        setBusy(false);
                      }
                    }}
                  >
                    Reload latest
                  </Button>
                ) : null}
              </div>
            ) : null}
            <div className="gift-contacts-panel__actions">
              {originalRecipient ? (
                <IconButton
                  icon={Trash2}
                  label="Unlink recipient"
                  variant="quiet"
                  disabled={busy}
                  onClick={() => void save(true)}
                />
              ) : null}
              <Button
                variant="primary"
                onClick={() => void save()}
                disabled={!dirty || busy || loading || loadError || (!selectedId && !canCreate)}
                loading={busy}
              >
                {originalRecipient ? 'Save recipient' : 'Add recipient'}
              </Button>
            </div>
          </footer>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
